import * as React from 'react'
import { useLocation } from '@reach/router'
import SEO from '@dfb/gatsby-dfb-theme/src/components/modules/seo'
import MAnchorNavigation from '@dfb/gatsby-dfb-theme/src/components/modules/m_anchor_navigation'
import MSocialMediaSubBrand from '@dfb/gatsby-dfb-theme/src/components/blocks/m_social_media_subbrand'
import { useLanguage } from '@dfb/gatsby-dfb-theme/src/hooks/useLanguage'
import WebComponentFussballliebe from '../../../../components/modules/web_component_fussballliebe'
import MHero from '../blocks/m_hero'
import MHeroCollectionSlider from '../blocks/m_hero_collection_slider'
import Layout from '../layout'
import Blocks from '../blocks'
import MSponsoring from '../blocks/m_sponsoring.js'
import { getOGTitle, getOGDescription_short, getOGImage } from '../../../../feed/common_og_data.mjs'
import WebComponentSubNavigation from '../../../../components/modules/web_component_sub_navigation.js'
import MHeroSlider from '../../../../components/blocks/m_hero_slider'
import { getAdId } from '../../../../utils/ads'

type SpecialAdditionalPageContent = Record<
  number,
  Array<{
    placement: number
    block: {
      layout: string
      anchor_name: string
      space_before_class: string
      space_after_class: string
      component: string
    }
  }>
>

// sometimes we have special components which we will only use once
// so we add them here...
const specialAdditionalPageContent: SpecialAdditionalPageContent = {
  [process.env.AUDIODESCRIPTIVE_REPORTAGE_UID]: [
    {
      placement: 1,
      block: {
        layout: 'default',
        component: 'm_mycrocast',
        anchor_name: '',
        space_before_class: 'default',
        space_after_class: 'default',
      },
    },
  ],
}

const ContentPage = (props) => {
  const language = useLanguage()
  const { parents } = props

  // If card news is present, the partners are shown there and not at the bottom of the page
  const cardNews = props.content.body.find((item) => item.component === 'm_card_news' || item.component === 'm_card_teaser')
  if (cardNews) {
    cardNews.partners = {
      headline: props.content.tx_headless_partner_banner_title,
      premiumPartners: props.content.tx_headless_premium_sponsor_links,
      partners: props.content.tx_headless_sponsor_links,
    }
  }

  const isHome = props.page.uid == process.env.HOMEPAGE_UID || props.page.uid == process.env.HOMEPAGE_UID_EN
  const isFussballliebe = props.page.uid == process.env.FUSSBALLLIEBE_UID

  const homeAdId = getAdId('sonderwerbemittel')

  // Workaround for "/content/" pages, where the big header should be displayed.
  const location = useLocation()
  const isContentPage = location.pathname.includes('/content/')
  const isSubPage = parents && parents.length > (isContentPage ? 2 : 1)

  const body = props.content.body
  specialAdditionalPageContent[props.page.uid]?.forEach((element) => body.splice(element.placement, 0, element.block))

  return (
    <Layout
      translations={props.translations}
      pageType={props.pageType}
      firstContentElement={props.content.body && props.content.body[0] ? props.content.body[0] : {}}
      accessGroups={props.accessGroups}
      pageUid={props.page.uid}
      brand={props.page.brand}
      language={language}
    >
      {props.metadata && (
        <SEO
          pagetitle={props.content.title}
          title={getOGTitle(props.metadata, props.content)}
          keywords={props.metadata.keywords ? props.metadata.keywords.split(',') : []}
          ogdescription={getOGDescription_short(props.metadata, props.content)}
          ogimage={getOGImage(props.metadata, props.content)?.src}
          noindex={props.metadata.noindex ? props.metadata.noindex : 0}
        />
      )}

      <main className="l-Main" role="main">
        {isHome && props.content.tx_headless_hero ? (
          <MHeroCollectionSlider data={props.content.tx_headless_hero} />
        ) : isFussballliebe && props.content.tx_headless_hero ? (
          <WebComponentFussballliebe />
        ) : props.content.tx_headless_hero && props.content.tx_headless_hero.length === 1 ? (
          <MHero
            data={props.content.tx_headless_hero}
            isBrand={props.brand !== 'dfb' && props.brand !== 'default'}
            brand={props.page.brand}
            isSubPage={isSubPage}
          ></MHero>
        ) : props.content.tx_headless_hero && props.content.tx_headless_hero.length > 1 ? (
          <MHeroSlider data={props.content.tx_headless_hero}></MHeroSlider>
        ) : null}
        {props.page.brand && props.page.brand !== 'default' && props.page.brand !== 'dfb' && (
          <WebComponentSubNavigation brand={props.page.brand} fullSlug={props.page.full_slug} language={language} />
        )}

        {isHome && homeAdId && <div id={homeAdId} />}

        {body &&
          body.map &&
          body.map((block) =>
            Blocks[block.component]
              ? React.createElement(Blocks[block.component], {
                  key: block.uid,
                  data: {
                    ...block,
                    root_categories: props.content.root_categories,
                    brand: props.page.brand,
                  },
                  pageData: {},
                })
              : `Block ${block.component} in ${block.uid} not created yet`
          )}

        {!cardNews && props.content.tx_headless_sponsor_links && props.content.tx_headless_sponsor_links.length > 0 && (
          <MSponsoring
            data={{
              headline: props.content.tx_headless_partner_banner_title,
              premiumPartners: props.content.tx_headless_premium_sponsor_links,
              partners: props.content.tx_headless_sponsor_links,
            }}
          />
        )}
        {props.content.social_media_links && props.content.social_media_links.length > 0 && (
          <MSocialMediaSubBrand
            title={props.content.social_media_links_title}
            socialMediaLinks={props.content.social_media_links}
            add_class="m-SocialMediaSubbrand--footer"
          />
        )}
        {!!props.page.skip_mark_navigation && <MAnchorNavigation sections={props.content.body} />}
      </main>
    </Layout>
  )
}

export default ContentPage
